import { Injectable, EventEmitter } from '@angular/core';
import { Config } from './config';
import { APISaveLocationGroup, LocationGroup, LocationGroupSelector } from '../models/location-group';
import { Observable, of, Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class LocationGroupService {
    public locationGroupChange = new EventEmitter<number>();

    public locationGroupEdited = new BehaviorSubject<number>(null);
    public locationGroupDeleted = new BehaviorSubject<number>(null);

    public locationGroupSelected = new BehaviorSubject<{ locationGroups: LocationGroup[] }>(null);

    public globalSelectableLocationGroups: Array<LocationGroupSelector> = [];

    /**
     * Represents the state of the location group selector in top-nav
     */
    private locationGroupSelectStatus = new BehaviorSubject<boolean>(false);

    private _locationGroupID: number;
    /** triggered on create location group or update existing, subscribers will update location group lists */
    public locGroupListUpdate$ = new Subject<void>();

    private locationGroupCache?: {
        customerId: number;
        res$?: Observable<{ locationGroups: LocationGroup[] }>;
        res?: { locationGroups: LocationGroup[] }
    }

    constructor(public http: HttpClient) {}

    /**
     * Gets the location group disabled state.
     */
    public get isLocationGroupDisabled(): BehaviorSubject<boolean> {
        return this.locationGroupSelectStatus;
    }

    /**
     * Sets the location group disabled state.
     */
    public set locationGroupDisabledStatus(isDisabled: boolean) {
        this.locationGroupSelectStatus.next(isDisabled);
    }

    // getter and setter for locationGroupId
    public set locationGroupID(locationGroupID: number) {
        this._locationGroupID = locationGroupID;
        this.locationGroupChange.emit(locationGroupID);
    }

    public get locationGroupID(): number {
        return this._locationGroupID;
    }

    // Add & update location Group
    public postLocationGroup(locationGroup: APISaveLocationGroup) {
        const postUrl = Config.getUrl(Config.urls.locationGroup.postUrl);

        if (!locationGroup.locationGroupID) {
            return this.http.post<LocationGroup>(postUrl, locationGroup).pipe(
                map((res) => {
                    this.locationGroupCache = null;
                    this.locGroupListUpdate$.next(null);
                    this.locationGroupEdited.next(locationGroup.locationGroupID);
                    return res;
                }),
            );
        } else {
            return this.http.put<LocationGroup>(postUrl, locationGroup).pipe(
                map((response) => {
                    this.locationGroupCache = null;
                    this.locGroupListUpdate$.next(null);
                    this.updateCachedLocationGroup(locationGroup);
                    this.locationGroupEdited.next(locationGroup.locationGroupID);
                    return response;
                }),
            );
        }
    }

    public getLocationGroups(customerId: number) {
        const getURL = Config.getUrl(Config.urls.locationGroup.getUrl + '?customerId=' + customerId);
        if(this.locationGroupCache && this.locationGroupCache.customerId === customerId) {
            if(this.locationGroupCache.res) {
                return of(this.locationGroupCache.res);
            } else {
                return this.locationGroupCache.res$;
            }
        } else {
            if(!this.locationGroupCache) {
                this.locationGroupCache = {
                    customerId: customerId,
                }
            } else if(this.locationGroupCache.customerId !== customerId) {
                this.locationGroupCache.customerId = customerId;
                this.locationGroupCache.res = null;
            }

            const getLocGroups$ = this.http.get<{ locationGroups: LocationGroup[] }>(getURL).pipe(
                tap((response) => {
                    if (response.locationGroups) {
                        this.clearCachedLocationGroups();
                        response.locationGroups.forEach(v => this.addCachedLocationGroup({...v, locations: v.locations.map(l => l.locationID)}));
                    }
                    this.locationGroupCache.res = response;
                    this.locationGroupSelected.next(response);
                }),
            );

            this.locationGroupCache.res$ = getLocGroups$;

            return getLocGroups$;
        }
    }

    public getRainLocationGroups(customerId: number) {
        const getURL = Config.urls.rainLocGroups + '?cid=' + customerId;
        return this.http.get<{ locationGroups: LocationGroup[] }>(getURL);
    }

    public deleteLocationGroups(locationGroupID: number) {
        this.locationGroupCache = null;
        this.removeCachedLocationGroup(locationGroupID);
        return this.http
            .delete<LocationGroup>(Config.getUrl(Config.urls.locationGroup.deleteUrl + locationGroupID))
            .pipe(map((response) => {
                this.locationGroupCache = null;
                this.locGroupListUpdate$.next(null);
                this.locationGroupDeleted.next(locationGroupID);
                return response;
            }));
    }

    public getLocationGroupDetails(customerId: number, locationGroupId: number) {
        const getURL = Config.getUrl(
            Config.urls.locationGroup.getUrl + '/' + locationGroupId + '?customerId=' + customerId,
        );
        return this.http.get<LocationGroup>(getURL).pipe(map((response) => <LocationGroup>response));
    }

    public markActiveLocationGroup(locationGroupId: number, locationGroups: Array<LocationGroup>): void {
        if (!locationGroupId) {
            return;
        }

        for (let i = 0; i < locationGroups.length; i++) {
            const group = locationGroups[i];

            if (group.locationGroupID === locationGroupId) {
                group.isCurrentlySelected = true;

                break;
            }
        }
    }

    private createLocationGroupSelector(locationGroup: APISaveLocationGroup) {
        const groupSelector: LocationGroupSelector = {
            id: locationGroup.locationGroupID,
            locations: locationGroup.locations,
            name: locationGroup.name,
            description: locationGroup.description,
            isChecked: false,
        };

        return groupSelector;
    }

    private addCachedLocationGroup(locationGroup: APISaveLocationGroup) {
        this.globalSelectableLocationGroups.push(this.createLocationGroupSelector(locationGroup));
    }

    private removeCachedLocationGroup(locationGroupID: number) {
        const groupIndex = this.globalSelectableLocationGroups.findIndex(lg => lg.id === locationGroupID);
        this.globalSelectableLocationGroups.splice(groupIndex, 1);
    }

    private updateCachedLocationGroup(locationGroup: APISaveLocationGroup) {
        const groupIndex = this.globalSelectableLocationGroups.findIndex(lg => lg.id === locationGroup.locationGroupID);
        const lg = this.createLocationGroupSelector(locationGroup);
        this.globalSelectableLocationGroups[groupIndex] = lg;
    }

    private clearCachedLocationGroups() {
        this.globalSelectableLocationGroups = new Array<LocationGroupSelector>();
    }

    public setLocationGroupsGlobalSelectable(locationGroups: APISaveLocationGroup[]) {
        this.clearCachedLocationGroups();

        this.globalSelectableLocationGroups.push({
            id: 0,
            locations: [],
            name: 'All',
            description: '',
            isChecked: false,
        });

        if (locationGroups) {
            locationGroups.forEach((locationGroup) => {
                this.addCachedLocationGroup(locationGroup);
            });
        }
    }

}
